import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { 
  Key, 
  Plus, 
  Trash2, 
  Copy, 
  Eye, 
  EyeOff, 
  Loader2, 
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Shield,
  Activity,
  Clock,
  XCircle,
  CheckCircle2
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExternalAPIKeysManager = () => {
  const queryClient = useQueryClient();
  const token = localStorage.getItem('token');
  
  const [selectedCompany, setSelectedCompany] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newKeyName, setNewKeyName] = useState('Vector Bridge Integration');
  const [newKeyExpiresDays, setNewKeyExpiresDays] = useState('');
  const [createdKey, setCreatedKey] = useState(null);
  const [showKey, setShowKey] = useState(false);

  // Fetch companies
  const { data: companies = [] } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      const response = await axios.get(`${API}/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    enabled: !!token
  });

  // Fetch API keys for selected company
  const { data: apiKeysData, isLoading: keysLoading, refetch: refetchKeys } = useQuery({
    queryKey: ['external-api-keys', selectedCompany],
    queryFn: async () => {
      const response = await axios.get(`${API}/admin/external-api-keys/${selectedCompany}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    enabled: !!token && !!selectedCompany
  });

  const apiKeys = apiKeysData?.keys || [];

  // Fetch external executions (logs)
  const { data: executionsData, isLoading: execLoading, refetch: refetchExecs } = useQuery({
    queryKey: ['external-executions', selectedCompany],
    queryFn: async () => {
      const response = await axios.get(`${API}/admin/external-executions/${selectedCompany}?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    enabled: !!token && !!selectedCompany,
    refetchInterval: 30000 // Auto refresh every 30s
  });

  const executions = executionsData?.executions || [];

  // Create API key mutation
  const createKeyMutation = useMutation({
    mutationFn: async () => {
      const params = new URLSearchParams();
      params.append('company_id', selectedCompany);
      params.append('name', newKeyName);
      if (newKeyExpiresDays) {
        params.append('expires_days', newKeyExpiresDays);
      }
      
      const response = await axios.post(
        `${API}/admin/external-api-keys?${params.toString()}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    onSuccess: (data) => {
      setCreatedKey(data.api_key);
      toast.success('API Key creada exitosamente');
      queryClient.invalidateQueries(['external-api-keys', selectedCompany]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Error al crear API Key');
    }
  });

  // Revoke API key mutation
  const revokeKeyMutation = useMutation({
    mutationFn: async (keyId) => {
      const response = await axios.delete(
        `${API}/admin/external-api-keys/${keyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success('API Key revocada');
      queryClient.invalidateQueries(['external-api-keys', selectedCompany]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Error al revocar API Key');
    }
  });

  // Delete execution mutation
  const deleteExecutionMutation = useMutation({
    mutationFn: async (executionId) => {
      const response = await axios.delete(
        `${API}/admin/external-executions/${executionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success('Ejecución eliminada');
      queryClient.invalidateQueries(['external-executions', selectedCompany]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Error al eliminar ejecución');
    }
  });

  // Cancel all stuck executions mutation
  const cancelStuckMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post(
        `${API}/admin/external-executions/cancel-stuck?company_id=${selectedCompany}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(`${data.cancelled_count} ejecuciones canceladas`);
      queryClient.invalidateQueries(['external-executions', selectedCompany]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Error al cancelar ejecuciones');
    }
  });

  const handleCreateKey = () => {
    if (!selectedCompany) {
      toast.error('Selecciona una empresa primero');
      return;
    }
    createKeyMutation.mutate();
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado al portapapeles');
  };

  const handleCloseCreateDialog = () => {
    setShowCreateDialog(false);
    setCreatedKey(null);
    setNewKeyName('Vector Bridge Integration');
    setNewKeyExpiresDays('');
  };

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card className="bg-gradient-to-r from-purple-900/50 to-blue-900/50 border-purple-500/30">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-600 rounded-lg">
              <Key className="w-6 h-6 text-white" />
            </div>
            <div>
              <CardTitle className="text-white text-xl">API Keys para Integraciones Externas</CardTitle>
              <CardDescription className="text-slate-300">
                Gestiona las API Keys para que Vector Bridge y otros sistemas puedan conectarse a Vector Solutions
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Company Selector & Actions */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="pt-6">
          <div className="flex items-end gap-4">
            <div className="flex-1 max-w-md">
              <Label className="text-slate-300 mb-2 block">Seleccionar Empresa (Tenant)</Label>
              <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                  <SelectValue placeholder="Selecciona una empresa..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <Button
              onClick={() => setShowCreateDialog(true)}
              disabled={!selectedCompany}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nueva API Key
            </Button>
            
            {selectedCompany && (
              <Button
                variant="outline"
                onClick={() => refetchKeys()}
                className="border-slate-600"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* API Keys Table */}
      {selectedCompany && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Shield className="w-5 h-5 text-purple-400" />
              API Keys de {companies.find(c => c.id === selectedCompany)?.name}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {keysLoading ? (
              <div className="flex justify-center p-8">
                <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
              </div>
            ) : apiKeys.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <Key className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No hay API Keys para esta empresa</p>
                <p className="text-sm mt-2">Crea una para permitir integraciones externas</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700">
                    <TableHead className="text-slate-400">Nombre</TableHead>
                    <TableHead className="text-slate-400">API Key (Preview)</TableHead>
                    <TableHead className="text-slate-400">Permisos</TableHead>
                    <TableHead className="text-slate-400">Estado</TableHead>
                    <TableHead className="text-slate-400">Creada</TableHead>
                    <TableHead className="text-slate-400">Último Uso</TableHead>
                    <TableHead className="text-slate-400">Expira</TableHead>
                    <TableHead className="text-slate-400">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {apiKeys.map((key) => (
                    <TableRow key={key.id} className="border-slate-700">
                      <TableCell className="text-white font-medium">{key.name}</TableCell>
                      <TableCell>
                        <code className="text-purple-400 bg-slate-900 px-2 py-1 rounded text-xs">
                          {key.api_key_preview}
                        </code>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {key.permissions?.map((perm) => (
                            <Badge key={perm} variant="outline" className="text-xs border-slate-600 text-slate-300">
                              {perm}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        {key.is_active ? (
                          <Badge className="bg-green-600">Activa</Badge>
                        ) : (
                          <Badge className="bg-red-600">Revocada</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-400 text-sm">
                        {key.created_at ? new Date(key.created_at).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell className="text-slate-400 text-sm">
                        {key.last_used ? new Date(key.last_used).toLocaleDateString() : 'Nunca'}
                      </TableCell>
                      <TableCell className="text-slate-400 text-sm">
                        {key.expires_at ? new Date(key.expires_at).toLocaleDateString() : 'No expira'}
                      </TableCell>
                      <TableCell>
                        {key.is_active && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (window.confirm('¿Revocar esta API Key? Esta acción no se puede deshacer.')) {
                                revokeKeyMutation.mutate(key.id);
                              }
                            }}
                            className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* External Executions Logs */}
      {selectedCompany && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <Activity className="w-5 h-5 text-blue-400" />
                Logs de Ejecuciones API Externa
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => refetchExecs()} className="text-slate-400">
                <RefreshCw className="w-4 h-4" />
              </Button>
              {executions.some(e => e.status === 'in_progress') && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => {
                    if (window.confirm('¿Cancelar todas las ejecuciones en progreso?')) {
                      cancelStuckMutation.mutate();
                    }
                  }}
                  className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
                  disabled={cancelStuckMutation.isLoading}
                >
                  {cancelStuckMutation.isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <XCircle className="w-4 h-4 mr-1" />
                      Cancelar colgadas
                    </>
                  )}
                </Button>
              )}
            </div>
            <CardDescription className="text-slate-400">
              Historial de ingestas y ejecuciones de agentes vía API externa
            </CardDescription>
          </CardHeader>
          <CardContent>
            {execLoading ? (
              <div className="flex justify-center p-6">
                <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
              </div>
            ) : executions.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <Activity className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p>No hay ejecuciones registradas</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {executions.map((exec) => (
                  <div key={exec.id} className={`p-3 rounded-lg border ${
                    exec.status === 'completed' ? 'bg-green-900/20 border-green-500/30' :
                    exec.status === 'error' ? 'bg-red-900/20 border-red-500/30' :
                    exec.status === 'in_progress' ? 'bg-blue-900/20 border-blue-500/30' :
                    exec.status === 'skipped' ? 'bg-yellow-900/20 border-yellow-500/30' :
                    'bg-slate-900/50 border-slate-700'
                  }`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2">
                        {exec.status === 'completed' ? <CheckCircle2 className="w-4 h-4 text-green-400" /> :
                         exec.status === 'error' ? <XCircle className="w-4 h-4 text-red-400" /> :
                         exec.status === 'in_progress' ? <Loader2 className="w-4 h-4 text-blue-400 animate-spin" /> :
                         exec.status === 'skipped' ? <AlertCircle className="w-4 h-4 text-yellow-400" /> :
                         <Clock className="w-4 h-4 text-slate-400" />}
                        <span className="text-sm font-medium text-white">{exec.module_type || 'N/A'}</span>
                        <Badge variant="outline" className={`text-xs ${
                          exec.status === 'completed' ? 'border-green-500 text-green-400' :
                          exec.status === 'error' ? 'border-red-500 text-red-400' :
                          exec.status === 'in_progress' ? 'border-blue-500 text-blue-400' :
                          exec.status === 'skipped' ? 'border-yellow-500 text-yellow-400' :
                          'border-slate-500 text-slate-400'
                        }`}>{exec.status}</Badge>
                        {exec.records_count > 0 && (
                          <span className="text-xs text-slate-500">{exec.records_count} registros</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500">
                          {exec.created_at ? new Date(exec.created_at).toLocaleString() : '-'}
                        </span>
                        {/* Delete button for stuck executions */}
                        {(exec.status === 'in_progress' || exec.status === 'error') && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-6 w-6 p-0 text-red-400 hover:text-red-300 hover:bg-red-900/30"
                            onClick={() => {
                              if (window.confirm('¿Eliminar esta ejecución?')) {
                                deleteExecutionMutation.mutate(exec.id);
                              }
                            }}
                            disabled={deleteExecutionMutation.isLoading}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    {/* Error message */}
                    {exec.error && (
                      <div className="mt-2 p-2 bg-red-900/30 rounded text-xs text-red-300">
                        <strong>Error:</strong> {exec.error}
                      </div>
                    )}
                    
                    {/* Detailed logs */}
                    {exec.logs && exec.logs.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {exec.logs.map((log, idx) => (
                          <div key={idx} className={`text-xs px-2 py-1 rounded ${
                            log.level === 'error' ? 'bg-red-900/20 text-red-300' :
                            log.level === 'warning' ? 'bg-yellow-900/20 text-yellow-300' :
                            'bg-slate-800/50 text-slate-400'
                          }`}>
                            <span className="opacity-60">[{new Date(log.time).toLocaleTimeString()}]</span> {log.message}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Agents info */}
                    {exec.agents && exec.agents.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {exec.agents.map((agent, idx) => (
                          <Badge key={idx} variant="outline" className={`text-xs ${
                            agent.status === 'completed' ? 'border-green-500/50 text-green-400' :
                            agent.status === 'error' ? 'border-red-500/50 text-red-400' :
                            agent.status === 'running' ? 'border-blue-500/50 text-blue-400' :
                            'border-slate-600 text-slate-400'
                          }`}>
                            {agent.name?.split('-')[1] || agent.name}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Usage Instructions */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-lg">Instrucciones de Uso</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <h4 className="text-white font-medium">Endpoints Disponibles</h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-600 text-xs">GET</Badge>
                  <code className="text-slate-300">/api/external/health</code>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-600 text-xs">GET</Badge>
                  <code className="text-slate-300">/api/external/modules</code>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-blue-600 text-xs">POST</Badge>
                  <code className="text-slate-300">/api/external/ingest</code>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-blue-600 text-xs">POST</Badge>
                  <code className="text-slate-300">/api/external/execute-agents</code>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-600 text-xs">GET</Badge>
                  <code className="text-slate-300">/api/external/status/{'{id}'}</code>
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <h4 className="text-white font-medium">Ejemplo de Request</h4>
              <pre className="bg-slate-900 p-3 rounded-lg text-xs text-slate-300 overflow-x-auto">
{`curl -X POST "/api/external/ingest" \\
  -H "X-API-Key: vs_live_xxx..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "module_type": "finance",
    "data": [
      {"kpi_name": "ROE", "value": 15.3}
    ],
    "auto_execute_agents": true
  }'`}
              </pre>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Create API Key Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={handleCloseCreateDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-purple-400" />
              {createdKey ? 'API Key Creada' : 'Crear Nueva API Key'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {createdKey 
                ? 'Copia y guarda esta API Key. No se puede recuperar después de cerrar este diálogo.'
                : 'Esta API Key permitirá a sistemas externos conectarse a Vector Solutions.'}
            </DialogDescription>
          </DialogHeader>

          {createdKey ? (
            <div className="space-y-4">
              <div className="p-4 bg-green-900/20 border border-green-500/30 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span className="text-green-400 font-medium">API Key generada exitosamente</span>
                </div>
                <div className="relative">
                  <Input
                    value={showKey ? createdKey : '•'.repeat(createdKey.length)}
                    readOnly
                    className="bg-slate-900 border-slate-600 text-white font-mono pr-20"
                  />
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowKey(!showKey)}
                      className="h-7 w-7 p-0"
                    >
                      {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyToClipboard(createdKey)}
                      className="h-7 w-7 p-0"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
              <div className="p-3 bg-amber-900/20 border border-amber-500/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5" />
                  <p className="text-amber-300 text-sm">
                    <strong>Importante:</strong> Guarda esta API Key en un lugar seguro. 
                    No podrás verla de nuevo después de cerrar este diálogo.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label className="text-slate-300">Nombre de la API Key</Label>
                <Input
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="Ej: Vector Bridge Production"
                  className="bg-slate-900 border-slate-600 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-300">Expiración (días)</Label>
                <Input
                  type="number"
                  value={newKeyExpiresDays}
                  onChange={(e) => setNewKeyExpiresDays(e.target.value)}
                  placeholder="Dejar vacío para no expirar"
                  className="bg-slate-900 border-slate-600 text-white mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Dejar vacío para crear una key sin expiración
                </p>
              </div>
              <div className="p-3 bg-slate-900 rounded-lg">
                <p className="text-sm text-slate-400">
                  <strong>Empresa:</strong> {companies.find(c => c.id === selectedCompany)?.name}
                </p>
                <p className="text-sm text-slate-400 mt-1">
                  <strong>Permisos:</strong> ingest, execute, read
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            {createdKey ? (
              <Button onClick={handleCloseCreateDialog} className="bg-purple-600 hover:bg-purple-700">
                Cerrar
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={handleCloseCreateDialog} className="border-slate-600">
                  Cancelar
                </Button>
                <Button 
                  onClick={handleCreateKey}
                  disabled={createKeyMutation.isPending}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {createKeyMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Key className="w-4 h-4 mr-2" />
                  )}
                  Crear API Key
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExternalAPIKeysManager;
